import { agendaDays, isoWeek } from './planning-helpers'

/**
 * Toolbar label of the visible agenda window: `S37 · 8 – 27 sept.`
 *
 * `S{n}` anchors the FIRST visible week (ISO-8601 week number) — the grid already prints a
 * « Semaine N » banner per week, so the toolbar only names where the window starts. The range then
 * spells out the first → last displayed day (Sundays excluded, same as the grid).
 *
 * Formatting rules (fr-BE, UTC):
 * - the year appears only when the window leaves `now`'s civil year (long-range navigation);
 * - the month is spelled out once when the window stays inside a single month (`8 – 27 sept.`).
 *
 * @param weekStart anchor of the window (snapped to its Monday by `agendaDays`)
 * @param weeks     window amplitude in weeks (1 / 2 / 3)
 * @param now       reference date for the "current year" rule — injectable for deterministic tests
 */
export function formatWindowLabel(weekStart: Date, weeks: number, now: Date = new Date()): string {
  const days = agendaDays(weekStart, weeks)
  const first = days[0]
  const last = days[days.length - 1]

  const currentYear = now.getUTCFullYear()
  const withYear = first.getUTCFullYear() !== currentYear || last.getUTCFullYear() !== currentYear
  const yearOpt: Intl.DateTimeFormatOptions = withYear ? { year: 'numeric' } : {}

  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString('fr-BE', { timeZone: 'UTC', ...opts })

  const sameMonth = first.getUTCFullYear() === last.getUTCFullYear()
    && first.getUTCMonth() === last.getUTCMonth()
  const from = sameMonth
    ? fmt(first, { day: 'numeric' })
    : fmt(first, { day: 'numeric', month: 'short', ...yearOpt })
  const to = fmt(last, { day: 'numeric', month: 'short', ...yearOpt })

  return `S${isoWeek(first)} · ${from} – ${to}`
}
